import type { RouteLocationNormalizedGeneric } from 'vue-router';
import { useAuthCookie } from '~/composables/useAuthCookie';
import { useDoceboAccessToken } from '~/composables/useDoceboAccessToken';
import { COOKIE_NAME_DOCEBO_ACCESS_TOKEN } from '~/types/cookieNames';
import { redirectToConsentFlow } from '~/composables/academy/redirectToConsentFlow';
import { getRoleAccessStatus, RoleAccessStatus } from '~/utils/roleAccess';
import { extractStatusCode } from '~/utils/extractStatusCodeFromError';
import { isQueryOnlyNavigation } from '~/utils/isQueryOnlyNavigation';
import log from '~/utils/log';
import { normalizeInternalPath } from '~/utils/normalizeInternalPath';

const refreshAttemptStateKey = 'docebo-session:refresh-attempted';

/**
 * Whether `/api/academy/docebo/authenticate` reported a refused account rather than a failure.
 * Outages, timeouts and misconfiguration keep their existing error handling.
 */
const isDoceboUserInactiveError = (error: unknown): boolean => {
    if (extractStatusCode(error) !== 403) return false;

    const data = (error as { data?: { error?: unknown } } | null)?.data;

    return data?.error === 'DoceboUserInactiveError';
};

/**
 * A filter click, for example, changes only the query. The page stays the same, so it must not authenticate again.
 * But the access token cookie expires, and the page needs it to load Docebo data. Without a token, the checks run to mint a new one.
 */
const canSkipSessionCheck = (to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedGeneric): boolean =>
    isQueryOnlyNavigation(to, from) && useDoceboAccessToken().hasDoceboAccessToken.value;

/**
 * The aim of this middleware is to ensure users which are using the Academy
 * have a valid Docebo session.
 */
export default defineNuxtRouteMiddleware(async (to, from) => {
    if (canSkipSessionCheck(to, from)) return;

    try {
        const { isKeycloakAuthenticated } = useLoginState(to, { includeShopware: false });
        const { redirectToLogin, removeRefreshAttempt } = useRedirectToLogin();
        const skipStoryblokLockCheck = to.meta.skipStoryblokLockCheck === true;
        let redirectTo: StoryblokMultilink | string | undefined;

        /**
         * Check if the page is locked and if a Docebo session is required
         * according to the configuration.
         */
        if (!skipStoryblokLockCheck) {
            const { rawPath } = useCleanStoryblokPath(to);
            const { data: story, error: storyError } = await useStory<PageComponentStoryblok>(rawPath, null, {
                route: to,
                disableFatalErrors: true
            });
            /**
             * We don't want to show the error toast/docebo_session_error on 404 pages.
             * The Docebo session is never required on non-locked pages.
             */
            if (storyError.value || !story.value?.content.locked) return;

            redirectTo = story.value.content.redirectTo;
        }

        const { data: config, error: configError } = await useConfig(to);
        if (configError.value || !config.value) throw new Error('Failed to load configuration.');

        /** The Docebo session is only required when this flag is set in the section config. */
        if (!config.value.content.lockedPagesRequireDoceboSession) return;

        /**
         * A valid Keycloak session is required for any Docebo session.
         */
        if (!isKeycloakAuthenticated.value) {
            if (redirectTo) {
                log.debug('[docebo-session.ts] Redirecting to locked page fallback.');
                return redirect(redirectTo, false, to);
            }

            log.debug('[docebo-session.ts] Redirecting to login.');
            return redirectToLogin({ to, refreshAttemptStateKey });
        }

        removeRefreshAttempt(refreshAttemptStateKey, to.fullPath);

        /**
         * The profession check comes first: users without an eligible role are sent to the locked
         * pages fallback by `redirectLockedPages.global` or `academy-access`, and must get there
         * without a consent detour.
         */
        if (getRoleAccessStatus(config.value.content.rolesWithAccess, useAuth().value) !== RoleAccessStatus.Allowed) {
            return;
        }

        /**
         * Runs on every locked page rather than being skipped when a Docebo cookie is present: the
         * cookie is client-writable and outlives a deactivation, so it does not prove the current
         * consent. The endpoint collapses repeated mints with a short server-side cache.
         */
        const { data: tokenResponse, error: tokenError } = await useFetch<{
            accessToken: string;
            expiresIn: number;
        }>('/api/academy/docebo/authenticate', {
            method: 'POST',
            credentials: 'include'
        });

        if (tokenError.value || !tokenResponse.value) {
            /** A refused token means the account is deactivated, i.e. the consent is missing. */
            if (isDoceboUserInactiveError(tokenError.value)) {
                log.debug('[docebo-session.ts] Docebo account not activated, redirecting to the consent flow.');
                return redirectToConsentFlow(config.value.content, to);
            }

            log.error('Failed to authenticate with Docebo:', tokenError.value);
            return redirect(redirectTo, true, to);
        }

        /**
         * Store the Docebo access token.
         */
        setDoceboAccessTokenCookie(tokenResponse.value.accessToken, tokenResponse.value.expiresIn);
    } catch (error) {
        log.error('[docebo-session.ts] An unexpected error occurred during Docebo authentication:', error);

        const { country, lang } = useLanguage(to);
        log.debug('[docebo-session.ts] Redirecting to homepage due to unexpected error.');
        return navigateTo(`/${country.value}-${lang.value}?docebo_session_error=1`);
    }
});

const redirect = (
    redirectTo?: StoryblokMultilink | string,
    isDoceboSessionError?: boolean,
    route?: RouteLocationNormalizedGeneric
) => {
    const { country, lang } = useLanguage(route);
    const runtimeConfig = useRuntimeConfig();

    if (!redirectTo) {
        log.debug('[docebo-session.ts] Redirecting to homepage because no redirectTo target is configured.');
        return navigateTo(`/${country.value}-${lang.value}${isDoceboSessionError ? '?docebo_session_error=1' : ''}`);
    }

    const countryLanguages = useCountryLanguages();
    const path =
        typeof redirectTo === 'string'
            ? useStoryblokUrl(redirectTo, route)
            : resolveStoryblokLink(redirectTo, countryLanguages, lang.value);
    const relativeUrl = new URL(path, runtimeConfig.public.baseUrl);
    if (isDoceboSessionError) relativeUrl.searchParams.set('docebo_session_error', '1');
    const redirectPath = `${relativeUrl.pathname}${relativeUrl.search}${relativeUrl.hash}`;

    log.debug('[docebo-session.ts] Redirecting to redirectTo target.');
    return navigateTo(normalizeInternalPath(redirectPath));
};

const setDoceboAccessTokenCookie = (accessToken: string, expiresIn: number) => {
    const doceboAccessTokenCookie = useAuthCookie(COOKIE_NAME_DOCEBO_ACCESS_TOKEN, {
        maxAge: expiresIn // The maxAge is always 3600 seconds. But, we implemented a refresh mechanism via `auth-refresh.client.ts`
    });

    doceboAccessTokenCookie.value = accessToken;
};
